import { useState, useEffect, useMemo } from 'react'
import { useLibraryStore } from '@/stores/library-store'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  LIBRARY_FOLDER_CATEGORY_LABELS,
  type LibraryFolderCategory,
  type LibraryFolder,
} from '@/lib/schemas'
import { Folder, FolderPlus, ChevronRight } from 'lucide-react'

const ROOT_PARENT = '__root__'

interface CreateFolderDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Pre-selects the parent. `null` = root. */
  defaultParentId: string | null
  /** Optional callback once the folder is created. Receives the new folder. */
  onCreated?: (folder: LibraryFolder) => void
}

/**
 * Unified folder creation dialog.
 *
 * Replaces the legacy CreateRootFolderDialog. Includes an explicit Location
 * picker (with hierarchical Root + folder tree options) and a live path
 * preview so the user always sees exactly where the folder will land.
 */
export function CreateFolderDialog({
  open,
  onOpenChange,
  defaultParentId,
  onCreated,
}: CreateFolderDialogProps) {
  const { folders, createFolder } = useLibraryStore()

  const [name, setName] = useState('')
  const [parentId, setParentId] = useState<string>(defaultParentId ?? ROOT_PARENT)
  const [category, setCategory] = useState<LibraryFolderCategory>('other')
  const [categoryDirty, setCategoryDirty] = useState(false)
  const [isCreating, setIsCreating] = useState(false)

  const parent = parentId === ROOT_PARENT ? null : folders.find((f) => f.id === parentId) ?? null
  const inheritedCategory: LibraryFolderCategory = parent?.category ?? 'other'

  // Reset all state when the dialog re-opens with a new default
  useEffect(() => {
    if (open) {
      setName('')
      setParentId(defaultParentId ?? ROOT_PARENT)
      setCategoryDirty(false)
    }
  }, [open, defaultParentId])

  // Auto-update category when parent changes, unless user has touched it
  useEffect(() => {
    if (!categoryDirty) setCategory(inheritedCategory)
  }, [inheritedCategory, categoryDirty])

  // Build hierarchical parent options (depth-first)
  const parentOptions = useMemo(() => {
    const out: { id: string; label: string; depth: number }[] = [
      { id: ROOT_PARENT, label: 'Root level', depth: 0 },
    ]
    const visit = (pid: string | null, depth: number) => {
      const children = folders
        .filter((f) => f.parentId === pid)
        .sort((a, b) => a.name.localeCompare(b.name))
      for (const child of children) {
        out.push({ id: child.id, label: child.name, depth })
        visit(child.id, depth + 1)
      }
    }
    visit(null, 1)
    return out
  }, [folders])

  const categoryOptions = Object.entries(LIBRARY_FOLDER_CATEGORY_LABELS) as [LibraryFolderCategory, string][]

  const grandparent = parent?.parentId
    ? folders.find((f) => f.id === parent.parentId) ?? null
    : null

  const handleCreate = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    setIsCreating(true)
    try {
      const created = await createFolder(
        trimmed,
        parentId === ROOT_PARENT ? null : parentId,
        category
      )
      onCreated?.(created)
      onOpenChange(false)
    } catch {
      // Error already surfaced via store toast/state
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New folder</DialogTitle>
          <DialogDescription>
            Folders organise documents by topic. The AI uses the category to
            prioritise evidence.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="folder-name">Folder name</Label>
            <Input
              id="folder-name"
              placeholder="e.g., Safety Policies"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && name.trim()) void handleCreate()
              }}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="folder-parent">Location</Label>
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger id="folder-parent">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {parentOptions.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id}>
                    <span
                      className="flex items-center gap-2"
                      style={{ paddingLeft: opt.depth * 12 }}
                    >
                      {opt.id === ROOT_PARENT ? (
                        <span className="text-muted-foreground">/</span>
                      ) : (
                        <Folder className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      {opt.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Live path preview */}
            <div className="flex items-center gap-1 text-meta pt-0.5">
              <span>Will be created at:</span>
              <span className="font-medium text-foreground inline-flex items-center gap-1">
                {parent ? (
                  <>
                    {grandparent && (
                      <>
                        <span>{grandparent.name}</span>
                        <ChevronRight className="h-3 w-3" />
                      </>
                    )}
                    <span>{parent.name}</span>
                    <ChevronRight className="h-3 w-3" />
                    <span className="text-muted-foreground italic">
                      {name.trim() || 'new folder'}
                    </span>
                  </>
                ) : (
                  <>
                    <span className="text-muted-foreground">/</span>
                    <span className="text-muted-foreground italic">
                      {name.trim() || 'new folder'}
                    </span>
                    <Badge variant="secondary" className="text-[9px] px-1 py-0 h-4 ml-1">
                      Root
                    </Badge>
                  </>
                )}
              </span>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="folder-category">Category</Label>
            <Select
              value={category}
              onValueChange={(v) => {
                setCategory(v as LibraryFolderCategory)
                setCategoryDirty(true)
              }}
            >
              <SelectTrigger id="folder-category">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {categoryOptions.map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-meta">
              {parent && !categoryDirty
                ? `Inherited from "${parent.name}". You can override.`
                : 'Used by the AI to prioritise this folder when relevant.'}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleCreate()}
            disabled={!name.trim() || isCreating}
          >
            <FolderPlus className="h-4 w-4 mr-2" />
            {isCreating ? 'Creating…' : 'Create folder'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
